import { HttpClient, HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { ApiEndpoints } from '../../core/api-endpoints.service';
import { AccessRequest, DataRecord, WorkflowState } from '../../core/workflow-state.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';

@Component({
  selector: 'app-access-id',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section aria-labelledby="access-id-title">
      <div class="section-heading">
        <div>
          <h2 id="access-id-title">Get Access ID</h2>
        </div>
      </div>

      <div class="workbench">
        <form class="form-surface" #accessIdForm="ngForm" (ngSubmit)="submit()">
          <div class="form-field">
            <label for="access-id-request">Pending booking</label>
            <select id="access-id-request" name="selectedId" class="form-select" [(ngModel)]="selectedId" required>
              <option value="" disabled>Select a pending booking</option>
              @for (item of pendingOptions(); track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" [disabled]="accessIdForm.invalid">Get Access ID</button>
          </div>
        </form>

        <div>
          <app-api-endpoint [endpoint]="endpoint" />

          <aside class="guidance-panel" aria-label="Access ID instructions">
            <p><strong>Description:</strong> Requests the Access ID for a booked lockedspace.</p>
            <p><strong>Expected Result:</strong> An Access ID means the selected lockedspace was booked successfully and the lock provider completed its callback. A negative response means there is no booking.</p>
          </aside>

          @if (pendingOptions().length === 0) {
            <div class="empty-state">There are no pending asynchronous bookings in this session.</div>
          }
          <app-api-result
            [endpoint]="endpoint"
            [showEndpoint]="false"
            [request]="call.request()"
            [response]="call.response()"
            [error]="call.error()"
          />
        </div>
      </div>
    </section>
  `,
})
export class AccessIdComponent {
  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly workflow = inject(WorkflowState);
  private readonly destroyRef = inject(DestroyRef);

  protected selectedId = '';
  protected readonly endpoint = `${this.endpoints.url(this.endpoints.accessId)}/{itemId}`;
  protected readonly call = new ApiCallState();
  protected readonly pendingOptions = computed(() =>
    Array.from(this.workflow.pendingAccessRequests(), ([id, value]) => ({
      id,
      label: [
        String(value['displayName'] ?? value['itemId'] ?? id),
        String(value['requestId'] ?? ''),
        nestedString(value, 'deliveryInstruction', 'lockOwnerId', 'emailAddress'),
      ]
        .filter(Boolean)
        .join(' | '),
    })),
  );

  protected submit(): void {
    const pending = this.workflow.pendingAccessRequests().get(this.selectedId);
    if (!pending) {
      return;
    }

    const itemId =
      nestedString(pending, 'deliveryInstruction', 'deliveryItem', 'id') ||
      String(pending['itemId'] ?? '');
    if (!itemId) {
      this.call.fail(new Error('The pending booking has no item ID.'));
      return;
    }

    this.call.start({ itemId });
    this.http
      .get<DataRecord>(`${this.endpoints.url(this.endpoints.accessId)}/${encodeURIComponent(itemId)}`, {
        observe: 'response',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.handleResponse(response, pending, itemId),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  private handleResponse(
    response: HttpResponse<DataRecord>,
    pending: DataRecord,
    itemId: string,
  ): void {
    if (response.status === 202) {
      this.call.succeed({ status: 202, requestId: pending['requestId'] });
      return;
    }

    const data: AccessRequest = {
      ...(response.body ?? {}),
      mapId: this.selectedId,
      systemProvider: pending['systemProvider'],
      itemId,
    };
    this.workflow.updateLockedSpace(this.selectedId, data);
    this.workflow.addAccessRequest(data);
    this.workflow.removePendingAccessRequest(this.selectedId);
    this.call.succeed({ accessId: data.accessId, reason: data['reason'] });
    this.selectedId = '';
  }
}

function nestedString(value: DataRecord, ...path: string[]): string {
  let current: unknown = value;
  for (const key of path) {
    if (!isRecord(current)) {
      return '';
    }
    current = current[key];
  }
  return typeof current === 'string' ? current : '';
}

function isRecord(value: unknown): value is DataRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}